import { useEffect, useState } from "react";

/**
 * Returns `false` on the mount commit, `true` one animation frame later.
 *
 * Use to defer non-critical row descendants (reactions, thread previews,
 * link previews, attachments) until after the row's text shell paints —
 * a row mount goes from 20-80ms (everything at once) to ~5-15ms for the
 * shell + a free post-paint hydration frame for the rest.
 *
 * Pattern:
 *   const hydrated = usePostMountFrame();
 *   return (
 *     <Row>
 *       <Sender />
 *       <Content />
 *       {hydrated && <Reactions />}
 *       {hydrated && <ThreadPreview />}
 *     </Row>
 *   );
 *
 * Intentional behavior on remount: each fresh fiber starts un-hydrated.
 * MessageRow is keyed by `messageId`, so within the same channel a row
 * keeps its hydrated state across re-renders. On channel switch (cap=1
 * single-mount), the messageList identity changes → React unmounts the
 * old rows and mounts new ones → fresh `false` → phase-1 paint, then
 * phase-2 hydration. That's the channel-switch win.
 */
export function usePostMountFrame(): boolean {
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (hydrated) return;
    const id = requestAnimationFrame(() => setHydrated(true));
    return () => cancelAnimationFrame(id);
  }, [hydrated]);
  return hydrated;
}
